import type { MyAccount } from '~/gql/account/account'
import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback } from '~/components/ui/avatar'
import { Card } from '~/components/ui/card'
import { brandBackdrop, TEXT_SAFE_TINT } from '~/helpers/branding/brand-backdrop'
import { tourAnchor } from '~/helpers/tour/anchors'
import { userDisplayName } from '~/helpers/users/display-name'
import { userInitials } from '~/helpers/users/initials'

/**
 * La carte « Mon profil » (#77) : un en-tête au fond de marque, l'avatar à
 * initiales à cheval dessus, puis les informations du compte.
 *
 * L'en-tête porte un titre : il prend donc l'intensité `TEXT_SAFE_TINT`, dont
 * les plafonds sont épinglés pour garder le texte AA quelle que soit la marque.
 * L'avatar reprend le couple primaire / encre, le seul au contraste garanti.
 */
function ProfileCard({ account }: { account: MyAccount }) {
  const { t, i18n } = useTranslation()

  const rows = [
    // Un apprenant sans e-mail (#271) se reconnaît à son pseudo.
    account.email ? { label: t('common.email'), value: account.email } : { label: t('common.username'), value: account.username },
    { label: t('common.role'), value: t(`role.${account.role}`) },
    account.organizationName ? { label: t('account.profile.organization'), value: account.organizationName } : null,
    {
      label: t('account.profile.lastConnection'),
      value: new Date(account.lastConnectionAt).toLocaleString(i18n.language, { dateStyle: 'long', timeStyle: 'short' }),
    },
  ].filter(row => row != null)

  return (
    <Card className="flex-1 overflow-hidden p-0" {...tourAnchor('account.profile')}>
      <div className="h-28 px-6 pt-5" style={{ background: brandBackdrop(TEXT_SAFE_TINT) }}>
        <h2 className="font-heading text-lg font-semibold">{t('account.profile.title')}</h2>
      </div>
      <div className="flex flex-col items-center px-6 pb-6">
        <Avatar className="-mt-10 size-20 rounded-full ring-4 ring-card">
          <AvatarFallback className="rounded-full bg-primary text-2xl font-semibold text-primary-foreground">
            {userInitials(account.firstName, account.lastName, account.username)}
          </AvatarFallback>
        </Avatar>
        <p className="mt-3 font-heading text-xl font-semibold">{userDisplayName(account)}</p>

        <dl className="mt-6 grid w-full gap-4">
          {rows.map(row => (
            <div key={row.label} className="grid gap-0.5">
              <dt className="text-xs text-muted-foreground">{row.label}</dt>
              <dd className="break-words text-sm font-medium">{row.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Card>
  )
}

export default ProfileCard
